// src/pages/admin/Transactions.jsx
// git commit: "redesign(Transactions): stat cards, detail panel, all types, mobile sheet"
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import Currency from "../../components/Currency";
import {
  Search,
  RefreshCw,
  X,
  ChevronRight,
  ArrowDownLeft,
  ArrowUpRight,
  RotateCcw,
  TrendingUp,
  Wallet,
  Receipt,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";

const TYPE_COLORS = {
  debit:      "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  purchase:   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  fund:       "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  credit:     "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  refund:     "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  rebuy:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  withdrawal: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
};

const TYPE_LABEL = {
  debit:      "Purchase",
  purchase:   "Purchase",
  fund:       "Fund",
  credit:     "Fund",
  refund:     "Refund",
  rebuy:      "Rebuy",
  withdrawal: "Withdrawal",
};

const isCredit = (type) => ["fund", "credit", "refund"].includes(type);

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get("/transactions");
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.transactions || [];
      setTransactions(data);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Keep selected in sync
  useEffect(() => {
    if (selected) {
      const updated = transactions.find((t) => t._id === selected._id);
      if (updated) setSelected(updated);
    }
  }, [transactions]);

  // Stats
  const stats = {
    total: transactions.length,
    totalFunded: transactions
      .filter((t) => ["fund", "credit"].includes(t.type))
      .reduce((s, t) => s + (t.amount || 0), 0),
    totalPurchases: transactions
      .filter((t) => ["debit", "purchase"].includes(t.type))
      .reduce((s, t) => s + (t.amount || 0), 0),
    totalRefunds: transactions
      .filter((t) => t.type === "refund")
      .reduce((s, t) => s + (t.amount || 0), 0),
  };

  // Filtered
  const filtered = transactions.filter((t) => {
    const matchType =
      typeFilter === "all" ||
      (typeFilter === "purchase" && ["debit", "purchase"].includes(t.type)) ||
      (typeFilter === "fund" && ["fund", "credit"].includes(t.type)) ||
      (typeFilter === "refund" && t.type === "refund") ||
      (typeFilter === "rebuy" && t.type === "rebuy");

    const matchSearch =
      !search.trim() ||
      (t.userId?.username || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.userId?.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.reference || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.description || "").toLowerCase().includes(search.toLowerCase());

    return matchType && matchSearch;
  });

  const copyRef = (ref) => {
    navigator.clipboard.writeText(ref).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const TYPE_FILTERS = [
    { key: "all", label: "All" },
    { key: "purchase", label: "Purchases" },
    { key: "fund", label: "Funding" },
    { key: "refund", label: "Refunds" },
    { key: "rebuy", label: "Rebuy" },
  ];

  return (
    <div className="space-y-5 h-full">
      {/* ── Stat Cards ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: "Total Transactions",
            value: stats.total,
            icon: Receipt,
            color: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-50 dark:bg-blue-900/20",
            isCount: true,
          },
          {
            label: "Total Funded",
            value: stats.totalFunded,
            icon: ArrowDownLeft,
            color: "text-green-600 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-900/20",
          },
          {
            label: "Total Purchases",
            value: stats.totalPurchases,
            icon: ArrowUpRight,
            color: "text-red-600 dark:text-red-400",
            bg: "bg-red-50 dark:bg-red-900/20",
          },
          {
            label: "Total Refunds",
            value: stats.totalRefunds,
            icon: RotateCcw,
            color: "text-amber-600 dark:text-amber-400",
            bg: "bg-amber-50 dark:bg-amber-900/20",
          },
        ].map(({ label, value, icon: Icon, color, bg, isCount }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4"
          >
            <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-3`}>
              <Icon size={16} className={color} />
            </div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {isCount ? value.toLocaleString() : <Currency amount={value} />}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Main panel ───────────────────────────────────── */}
      <div className="flex gap-5 min-h-0">
        {/* Left: list */}
        <div
          className={`flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-300 ${
            selected ? "w-full lg:w-3/5" : "w-full"
          }`}
        >
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 dark:border-gray-800 space-y-3">
            {/* Type pills */}
            <div className="flex gap-2 flex-wrap">
              {TYPE_FILTERS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTypeFilter(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    typeFilter === key
                      ? "bg-purple-600 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Search + refresh */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search user, reference, description..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>
              <button
                onClick={fetchTransactions}
                disabled={loading}
                className="p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition"
              >
                <RefreshCw
                  size={15}
                  className={`text-gray-500 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* Count row */}
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {transactions.length}
              </span>{" "}
              records
            </p>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw size={24} className="text-purple-500 animate-spin" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading transactions...</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="text-center">
                <Receipt size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No transactions found</p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                    <tr>
                      {["User", "Type", "Amount", "Description", "Date", ""].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {filtered.map((t) => (
                      <tr
                        key={t._id}
                        onClick={() =>
                          setSelected(selected?._id === t._id ? null : t)
                        }
                        className={`cursor-pointer transition-all ${
                          selected?._id === t._id
                            ? "bg-purple-50 dark:bg-purple-900/10"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                        }`}
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300 flex-shrink-0">
                              {t.userId?.username?.[0]?.toUpperCase() || "?"}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {t.userId?.username || "Unknown"}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[120px]">
                                {t.userId?.email || "—"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              TYPE_COLORS[t.type] || TYPE_COLORS.debit
                            }`}
                          >
                            {TYPE_LABEL[t.type] || t.type}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-sm font-bold">
                          <span
                            className={
                              isCredit(t.type)
                                ? "text-green-600 dark:text-green-400"
                                : "text-red-600 dark:text-red-400"
                            }
                          >
                            {isCredit(t.type) ? "+" : "−"}
                            <Currency amount={t.amount} />
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400 max-w-[180px] truncate">
                          {t.description || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-400 dark:text-gray-500">
                          {new Date(t.createdAt).toLocaleDateString("en-NG", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-5 py-3.5">
                          <ChevronRight
                            size={16}
                            className={`text-gray-300 transition-transform ${
                              selected?._id === t._id
                                ? "rotate-90 text-purple-500"
                                : ""
                            }`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800 overflow-y-auto">
                {filtered.map((t) => (
                  <button
                    key={t._id}
                    onClick={() =>
                      setSelected(selected?._id === t._id ? null : t)
                    }
                    className={`w-full text-left px-4 py-4 flex items-center gap-3 transition ${
                      selected?._id === t._id
                        ? "bg-purple-50 dark:bg-purple-900/10"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCredit(t.type)
                          ? "bg-green-100 dark:bg-green-900/30"
                          : "bg-red-100 dark:bg-red-900/30"
                      }`}
                    >
                      {isCredit(t.type) ? (
                        <ArrowDownLeft size={16} className="text-green-600 dark:text-green-400" />
                      ) : (
                        <ArrowUpRight size={16} className="text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {t.userId?.username || "Unknown"}
                        </p>
                        <span
                          className={`text-sm font-bold ml-2 flex-shrink-0 ${
                            isCredit(t.type)
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {isCredit(t.type) ? "+" : "−"}
                          <Currency amount={t.amount} />
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {TYPE_LABEL[t.type] || t.type} •{" "}
                        {new Date(t.createdAt).toLocaleDateString("en-NG", {
                          day: "numeric",
                          month: "short",
                        })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: Detail panel — desktop */}
        {selected && (
          <div className="hidden lg:flex flex-col w-2/5 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Transaction Details
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Amount hero */}
              <div
                className={`rounded-xl p-5 text-center ${
                  isCredit(selected.type)
                    ? "bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800"
                    : "bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800"
                }`}
              >
                <p
                  className={`text-3xl font-bold ${
                    isCredit(selected.type)
                      ? "text-green-700 dark:text-green-300"
                      : "text-red-700 dark:text-red-300"
                  }`}
                >
                  {isCredit(selected.type) ? "+" : "−"}
                  <Currency amount={selected.amount} />
                </p>
                <span
                  className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                    TYPE_COLORS[selected.type] || TYPE_COLORS.debit
                  }`}
                >
                  {TYPE_LABEL[selected.type] || selected.type}
                </span>
              </div>

              {/* User info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm flex-shrink-0">
                  {selected.userId?.username?.[0]?.toUpperCase() || "?"}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {selected.userId?.username || "Unknown"}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {selected.userId?.email || "—"}
                  </p>
                </div>
              </div>

              {/* Details grid */}
              <div className="space-y-3">
                {[
                  {
                    label: "Description",
                    value: selected.description || "—",
                  },
                  {
                    label: "Date",
                    value: new Date(selected.createdAt).toLocaleString("en-NG", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  },
                  {
                    label: "Status",
                    value: selected.status || "completed",
                  },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
                  >
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 pt-0.5">
                      {label}
                    </span>
                    <span className="text-xs font-medium text-gray-900 dark:text-white text-right capitalize">
                      {value}
                    </span>
                  </div>
                ))}

                {/* Reference with copy button */}
                <div className="flex items-center justify-between gap-3 py-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    Reference
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate max-w-[140px]">
                      {selected.reference || "—"}
                    </span>
                    {selected.reference && (
                      <button
                        onClick={() => copyRef(selected.reference)}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition flex-shrink-0"
                      >
                        {copied ? (
                          <Check size={12} className="text-green-500" />
                        ) : (
                          <Copy size={12} className="text-gray-400" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile detail sheet */}
      {selected && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <div className="px-5 pb-8 pt-3 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                  Transaction Details
                </h3>
                <button
                  onClick={() => setSelected(null)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              {/* Amount */}
              <div
                className={`rounded-xl p-4 text-center ${
                  isCredit(selected.type)
                    ? "bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800"
                    : "bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800"
                }`}
              >
                <p
                  className={`text-2xl font-bold ${
                    isCredit(selected.type)
                      ? "text-green-700 dark:text-green-300"
                      : "text-red-700 dark:text-red-300"
                  }`}
                >
                  {isCredit(selected.type) ? "+" : "−"}
                  <Currency amount={selected.amount} />
                </p>
                <span
                  className={`inline-block mt-1.5 px-3 py-0.5 rounded-full text-xs font-medium ${
                    TYPE_COLORS[selected.type] || TYPE_COLORS.debit
                  }`}
                >
                  {TYPE_LABEL[selected.type] || selected.type}
                </span>
              </div>

              {/* User */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm flex-shrink-0">
                  {selected.userId?.username?.[0]?.toUpperCase() || "?"}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {selected.userId?.username || "Unknown"}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {selected.userId?.email || "—"}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3">
                {[
                  { label: "Description", value: selected.description || "—" },
                  {
                    label: "Date",
                    value: new Date(selected.createdAt).toLocaleString("en-NG", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  },
                  { label: "Status", value: selected.status || "completed" },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800"
                  >
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {label}
                    </span>
                    <span className="text-xs font-medium text-gray-900 dark:text-white text-right capitalize">
                      {value}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 py-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Reference
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate max-w-[160px]">
                      {selected.reference || "—"}
                    </span>
                    {selected.reference && (
                      <button
                        onClick={() => copyRef(selected.reference)}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        {copied ? (
                          <Check size={12} className="text-green-500" />
                        ) : (
                          <Copy size={12} className="text-gray-400" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
